import React, { FC, useEffect, useState } from 'react';
import styles from "./FeedBacksBlock.module.scss";
import { IHotel, IPlace, IRestaurant } from "@common/redux/types/places";
import { IEvent } from "@common/redux/types/event";
import { FeedbackModal } from "@kursk/components/ui/FeedbacksBlock/FeedbackModal/FeedbackModal";
import { useSelectSlice } from "@common/redux/selectors/useSelectSlice";
import { authSlice } from "@common/redux/slices/auth.slice";
import { AuthModal } from "@kursk/components/Layout/AuthModal/AuthModal";
import { IArticle } from "@common/redux/types/articles";
import { Button } from "@kursk/components/ui/Button/Button";
import { IStream } from "@common/redux/types/invoices";
import { SuccessFeedback } from "@kursk/components/ui/FeedbacksBlock/SuccessFeedback";
import { checkCanSendFeedback, feedbacksSlice } from "@common/redux/slices/feedbacks.slice";
import { IFeedback } from "@common/redux/types/feedbacks";
import { FeedbackCard } from "@kursk/components/ui/FeedbacksBlock/FeedbackCard/FeedbackCard";
import { CarouselButtonGroup } from '@kursk/components/ui/CarouselButtonGroup/CarouselButtonGroup';
import { Carousel, ICarouselOptions } from "@kursk/components/ui/Carousel/Carousel";
import { ResponsiveType } from "react-multi-carousel";
import { EntityType } from "@common/redux/types/common";
import { useDispatchThunk } from "@common/redux/dispatchThunk";
import { useOrientations } from "@common/hooks/useOrientation";
import { prepareEntity } from "@kursk/utils/prepareFeedbackEntity";
import { IExcursion } from '@common/redux/types/excursion';
import { ITour } from '@common/redux/types/tour';

interface Props {
  entity: IEvent | IArticle | IPlace | IHotel | IRestaurant | IStream | IExcursion | ITour;
  entityType: EntityType;
}

export const FeedbacksBlock: FC<Props> = ({ entity, entityType }) => {
  const dispatchThunk = useDispatchThunk();
  const { user } = useSelectSlice(authSlice);
  const { feedbacksList, canSendFeedback } = useSelectSlice(feedbacksSlice);
  const [isOpenFeedbackModal, setIsOpenFeedbackModal] = useState(false);
  const [isOpenAuthModal, setIsOpenAuthModal] = useState(false);
  const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false);
  const orientation = useOrientations();

  const renderCard = (feedback: IFeedback, key: number) => {
    return (
      <FeedbackCard key={key} feedback={feedback}/>
    );
  };

  const responsive: ResponsiveType = {
    desktop1: {
      breakpoint: { max: 9999, min: 1200 },
      items: 3,
    },
    tablet2: {
      breakpoint: { max: 1199, min: 1020 },
      items: 2,
      partialVisibilityGutter: 100,
    },
    tablet3: {
      breakpoint: { max: 1020, min: 900 },
      items: 2,
      partialVisibilityGutter: 50,
    },
    tablet4: {
      breakpoint: { max: 900, min: 800 },
      items: 2,
    },
    tablet5: {
      breakpoint: { max: 799, min: 700 },
      items: 2,
    },
    mobile1: {
      breakpoint: { max: 699, min: 550 },
      items: 1,
    },
    mobile2: {
      breakpoint: { max: 549, min: 450 },
      items: 1,
      partialVisibilityGutter: feedbacksList?.feedbacks.length > 1 ? 100 : 0
    },
    mobile3: {
      breakpoint: { max: 449, min: 0 },
      items: 1,
    }
  };

  const openModal = () => {
    if (user) {
      setIsOpenFeedbackModal(true);
    } else {
      setIsOpenAuthModal(true);
    }
  };

  const options: ICarouselOptions = {
    responsive,
    swipeable: true,
    infinite: false,
    renderButtonGroupOutside: true,
    partialVisible: true,
    sliderClass: styles.carouselTrack
  };

  useEffect(() => {
    if (user) dispatchThunk(checkCanSendFeedback(prepareEntity(entityType, entity._id)));
  }, [user]);

  return (
    <div className={styles.wrapper}>
      <AuthModal isOpen={isOpenAuthModal} setIsOpen={setIsOpenAuthModal}/>
      <FeedbackModal
        entity={entity}
        entityType={entityType}
        isOpen={isOpenFeedbackModal}
        setIsOpen={setIsOpenFeedbackModal}
        user={user}
        setIsSuccessModalOpen={setIsSuccessModalOpen}
      />
      <SuccessFeedback isOpen={isSuccessModalOpen} setIsOpen={setIsSuccessModalOpen}/>
      <div className={styles.topWrapper}>
        <div className={styles.title}>Отзывы</div>
        <div className={styles.createWrapper}>
          <Button
            text='Оставить отзыв'
            onClick={openModal}
            disabled={user ? !canSendFeedback : false}
            buttonType='ghostButton'
          />
        </div>
      </div>
      {!!feedbacksList?.feedbacks.length && (<div className={styles.bottomWrapper}>
        <Carousel
          Buttons={feedbacksList?.feedbacks.length > (orientation === 'desktop' ? 3 : 1) && <CarouselButtonGroup/>}
          carouselOptions={options}
        >
          {feedbacksList?.feedbacks?.map(renderCard)}
        </Carousel>
      </div>
      )}
      {feedbacksList?.feedbacks.length === 0 && (
        <div className={styles.emptyContent}>
          <span>Отзывов пока нет, ваш может быть первым.</span>
        </div>
      )}
    </div>
  );
};
